import * as React from "react";

import twMerge from "@replo/design-system/utils/twMerge";
import { getHotKey } from "@v1/utils/getHotKey";
import isString from "lodash-es/isString";

type SelectionIndicatorProps = {
  title: React.ReactNode;
  startEnhancer?: React.ReactNode;
  endEnhancer?: React.ReactNode;
  onClick?(e: React.MouseEvent): void;
  /**
   * function to call when SelectionIndicator is option-clicked. If this is
   * undefined (or not passed), onClick will be called when option-clicked.
   */
  onReset?(e: React.MouseEvent): void;
  className?: string;
  labelClassname?: string;
  style?: React.CSSProperties;
  placeholder?: string;
  disabled?: boolean;
};

const SelectorTitle = ({
  title,
  labelClassname,
}: {
  title: React.ReactNode;
  labelClassname?: string;
}) => {
  return isString(title) ? (
    <div className="my-auto inline-grid w-full">
      <span
        className={twMerge("text-default truncate text-xs", labelClassname)}
      >
        {title}
      </span>
    </div>
  ) : (
    <>{title}</>
  );
};

const SelectionIndicator = React.forwardRef<
  HTMLDivElement,
  SelectionIndicatorProps
>(
  (
    {
      title,
      startEnhancer,
      endEnhancer,
      onClick,
      onReset,
      className,
      labelClassname,
      style,
      placeholder,
      disabled,
    },
    ref,
  ) => {
    const handleClick = (e: React.MouseEvent) => {
      if (onReset && getHotKey(e) === "altKey") {
        onReset(e);
      } else if (!disabled) {
        onClick?.(e);
      }
    };
    return (
      <div
        ref={ref}
        className={twMerge(
          "bg-sheet border-0.5 border-component-border flex h-6 w-full items-center gap-2 overflow-hidden whitespace-nowrap rounded-sm p-1",
          onClick && !disabled && "cursor-pointer",
          disabled && "cursor-not-allowed",
          className,
        )}
        onClick={handleClick}
        style={style}
      >
        {startEnhancer ? (
          <div className="flex shrink-0">{startEnhancer}</div>
        ) : null}
        <div className="flex w-full items-center overflow-hidden text-ellipsis">
          {placeholder && !title ? (
            <span
              className={twMerge(
                "text-muted my-auto grow text-start text-xs",
                labelClassname,
              )}
            >
              {placeholder}
            </span>
          ) : (
            <SelectorTitle title={title} labelClassname={labelClassname} />
          )}
        </div>
        {endEnhancer && (
          <div className="flex h-4 w-4 shrink-0 items-center justify-center">
            {endEnhancer}
          </div>
        )}
      </div>
    );
  },
);

SelectionIndicator.displayName = "SelectionIndicator";

export default SelectionIndicator;
